// src/components/feature/InvoiceDetailPanel.tsx
//
// Shared per-invoice drilldown used by:
//   - /admin/invoices   (one row per invoice, click to expand)
//   - /admin/customers  (a customer's recent-invoices list, click to expand)
//   - the per-customer Sales dashboard drilldown
//
// Fetches /api/admin/invoices/{invoice_number}/lines and renders the
// real invoice header + line items. Loaded lazily so the parent
// doesn't pay the cost for closed rows.

import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { authHeaders } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";
import { useInventoryText } from "@/lib/inventory/i18n";

export interface InvoiceLine {
  seq: number;
  product_id: number | null;
  ean: string | null;
  label: string | null;
  serial_number?: string | null;
  brand_report: string | null;
  quantity: number | string;
  unit_price_htva: number | string | null;
  unit_price_tvac: number | string | null;
  line_total_htva: number | string | null;
  line_total_tvac: number | string | null;
  image_path: string | null;
}

export interface InvoiceHeader {
  invoice_number: string;
  invoice_date: string | null;
  customer_id: number | null;
  customer_name_snapshot: string | null;
  payment_method: string | null;
  vat_exemption: string | null;
  total_htva: number | string | null;
  total_tva: number | string | null;
  total_tvac: number | string | null;
  total_discount_htva: number | string | null;
  line_count: number;
}

export interface InvoiceDetail {
  invoice: InvoiceHeader;
  lines: InvoiceLine[];
}

export interface InvoiceDetailPanelProps {
  invoiceNumber: string;
  /** Optional helper for the loading state copy — defaults to a
   *  neutral "Loading lines…". */
  loadingLabel?: string;
}

export function InvoiceDetailPanel({ invoiceNumber, loadingLabel }: InvoiceDetailPanelProps) {
  const [data, setData] = useState<InvoiceDetail | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError("");
    fetch(`/api/admin/invoices/${encodeURIComponent(invoiceNumber)}/lines`, {
      headers: authHeaders(),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as InvoiceDetail;
        if (!cancelled) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e instanceof Error ? e.message : e));
      });
    return () => { cancelled = true; };
  }, [invoiceNumber]);

  if (error) {
    return (
      <div className="border-t border-border bg-bg-canvas/40 p-4">
        <p className="text-xs text-accent-rose">
          Invoice <code className="font-mono">{invoiceNumber}</code> unavailable — {error}
        </p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="border-t border-border bg-bg-canvas/40 p-4">
        <p className="text-xs text-fg-tertiary">{loadingLabel ?? "Loading lines…"}</p>
      </div>
    );
  }
  return <LinesTable detail={data} />;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-fg-quaternary">{label}</div>
      <div className="font-510 text-fg-primary">{value}</div>
    </div>
  );
}

function LinesTable({ detail }: { detail: InvoiceDetail }) {
  const t = useInventoryText();
  const inv = detail.invoice;
  return (
    <div className="space-y-3 border-t border-border bg-bg-canvas/40 p-4">
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <Stat label="Date" value={inv.invoice_date ?? "—"} />
        <Stat label="Customer" value={inv.customer_name_snapshot ?? "—"} />
        <Stat label="Payment" value={inv.payment_method ?? "—"} />
        <Stat label="VAT regime" value={inv.vat_exemption ?? "—"} />
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-fg-quaternary">
            <th className="py-1 text-left">#</th>
            <th className="py-1 text-left">Product</th>
            <th className="py-1 text-left">Brand</th>
            <th className="py-1 text-right">Qty</th>
            <th className="py-1 text-right">{t.unitHtva}</th>
            <th className="py-1 text-right">{t.totalHtva}</th>
          </tr>
        </thead>
        <tbody>
          {detail.lines.map((l) => (
            <tr key={l.seq} className="border-b border-border/40">
              <td className="py-1 text-fg-quaternary">{l.seq}</td>
              <td className="py-1">
                <div className="flex items-center gap-2">
                  {l.image_path ? (
                    <img
                      src={`/api/products/${l.product_id}/image`}
                      alt=""
                      width={28}
                      height={28}
                      loading="lazy"
                      className="h-7 w-7 rounded object-cover"
                    />
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded bg-bg-elevated text-fg-quaternary">
                      <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="truncate text-fg-primary">{l.label ?? "—"}</div>
                    {l.ean && (
                      <div className="font-mono text-[10px] text-fg-quaternary">
                        {l.ean}
                      </div>
                    )}
                    <div className="text-[10px] text-fg-tertiary">
                      {t.invoiceSerialLabel}{" "}
                      <span className="whitespace-pre-wrap break-words font-mono">
                        {l.serial_number?.trim() ? l.serial_number : t.invoiceSerialMissing}
                      </span>
                    </div>
                  </div>
                </div>
              </td>
              <td className="py-1 text-fg-secondary">{l.brand_report ?? "—"}</td>
              <td className="py-1 text-right font-mono text-fg-primary">{Number(l.quantity)}</td>
              <td className="py-1 text-right font-mono text-fg-secondary">
                {formatPrice(Number(l.unit_price_htva ?? 0), "EUR")}
              </td>
              <td className="py-1 text-right font-mono text-fg-primary">
                {formatPrice(Number(l.line_total_htva ?? 0), "EUR")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-baseline justify-end gap-4 border-t border-border pt-2 text-xs">
        <span className="text-fg-tertiary">{t.netHtva}</span>
        <span className="font-mono text-fg-primary">
          {formatPrice(Number(inv.total_htva ?? 0), "EUR")}
        </span>
        <span className="text-fg-tertiary">TVA</span>
        <span className="font-mono text-fg-primary">
          {formatPrice(Number(inv.total_tva ?? 0), "EUR")}
        </span>
        <span className="text-fg-tertiary">{t.totalTvac}</span>
        <span className="font-mono text-base font-510 text-fg-primary">
          {formatPrice(Number(inv.total_tvac ?? 0), "EUR")}
        </span>
      </div>
    </div>
  );
}
